/**
 * Bewertung von Schlafplätzen.
 *
 * Der Punkt, an dem sich diese App von einem Hotelportal unterscheidet: Beim
 * Übernachten im Auto entscheiden nicht Sterne und Preis, sondern ob man dort
 * stehen darf, ob es eben und ruhig ist und ob eine Toilette in der Nähe ist.
 * Genau diese Angaben liefert OpenStreetMap — und genau danach fragt kein
 * Buchungsportal.
 *
 * **Landstrom kommt hier nicht vor.**
 *
 * Zwei Anläufe, zwei Irrtümer. Zuerst stand hier, eine Campingplatz-Steckdose
 * bringe über Nacht rund 40 kWh in die Batterie und erspare einen
 * Schnellladestopp — falsch, denn geladen würde über den Typ-2-Port, an dem
 * nachts V2L für das BiPAP hängt, und einen CEE-Ladeadapter gibt es nicht.
 * Danach stand hier, Landstrom sei wenigstens fürs BiPAP ein Gewinn, weil das
 * Gerät dann am Netz statt über V2L läuft.
 *
 * Auch das war eine Annahme über jemanden, der es anders macht: Strom auf
 * Stell- und Campingplätzen ist meist unverhältnismäßig teuer und wird
 * grundsätzlich nicht genutzt — nicht fürs BiPAP, nicht für sonst etwas.
 * Geladen wird ausschließlich an Schnellladesäulen. Eine Steckdose am Platz
 * ändert an der Nacht also nichts, und ein Platz, der dafür Punkte bekäme,
 * stünde zu Unrecht vor einem schöneren ohne.
 *
 * Das BiPAP läuft immer über V2L. Was daraus folgt — das 12-Stunden-Fenster,
 * die 20-%-Grenze — steht im Ladeplaner, nicht hier.
 *
 * Reine Funktionen, keine Abhängigkeiten — testbar und in M8 wiederverwendbar.
 */

import type { Poi } from './api';

export interface Bewertung {
  /** 0–100. Größer ist besser. */
  punkte: number;
  /** Was dafür spricht — wird unter dem Treffer angezeigt. */
  gruende: string[];
  /** Was dagegen spricht oder fehlt. */
  einwaende: string[];
}

/** „yes"/„designated" gelten als ja, alles andere als nein. */
function ja(wert: string | undefined): boolean {
  return wert === 'yes' || wert === 'designated';
}

export interface BewertungsKontext {
  /** Abstand zur Route bzw. zum Übernachtungspunkt in Metern. */
  abstand_m: number | null;
  /**
   * Wo in dieser Reise geschlafen wird.
   *
   * Fast alles, was diese Wertung von einem Hotelportal unterscheidet, gilt
   * nur fürs Übernachten **im Fahrzeug**: ob eine Toilette in der Nähe ist, ob
   * der Boden fest ist, ob man dort überhaupt stehen darf, was der Platz
   * kostet. Bei einer Reise mit Unterkunft ist das keine Auskunft, sondern
   * Lärm — ein Hotel ohne „WC" in der Begründung sähe aus, als fehlte ihm
   * etwas. Dann bleibt die Nähe, und die ist ehrlich.
   *
   * Fehlt die Angabe, gilt `fahrzeug` — das bisherige Verhalten.
   */
  uebernachtung?: 'fahrzeug' | 'unterkunft' | 'gemischt';
}

/**
 * Gilt für **diesen Treffer** der Maßstab fürs Übernachten im Fahrzeug?
 *
 * Bis `gemischt` hing das allein an der Reise, und das genügte: Zwei Antworten,
 * zwei Maßstäbe. Eine gemischte Reise hat beide Sorten Nächte, und eine
 * gemittelte Wertung wäre für beide falsch — ein Stellplatz ohne WC bekäme
 * seinen Einwand nicht mehr, und ein Hotel bekäme einen, der es nichts angeht.
 *
 * Deshalb entscheidet bei `gemischt` die **Kategorie des Treffers** statt der
 * Reise: Stellplatz und Campingplatz nach Sanitär, Untergrund, Gebühr und
 * Standerlaubnis, Unterkünfte nach Nähe. Für die beiden Bestandswerte ändert
 * sich dabei kein einziger Punkt — `fahrzeug` sagt weiter für jeden Treffer
 * ja, `unterkunft` für jeden nein.
 */
export function fahrzeugmassstab(
  uebernachtung: BewertungsKontext['uebernachtung'],
  subcategory: string | null | undefined
): boolean {
  if (uebernachtung === 'unterkunft') return false;
  if (uebernachtung === 'gemischt') return subcategory !== 'hotel';
  return true;
}

export function bewerte(p: Poi, kontext: BewertungsKontext): Bewertung {
  const a = (p.attrs ?? {}) as Record<string, string>;
  const gruende: string[] = [];
  const einwaende: string[] = [];
  const imFahrzeug = fahrzeugmassstab(kontext.uebernachtung, p.subcategory);

  // Grundwert: Nähe. Ein Schlafplatz 30 km abseits ist selten die bessere Wahl,
  // auch wenn er schöner ist — man kommt abends müde an.
  const km = (kontext.abstand_m ?? 0) / 1000;
  let punkte = Math.max(0, 40 - km * 3);
  if (km <= 2) gruende.push('direkt an der Route');
  else if (km > 15) einwaende.push(`${km.toFixed(0)} km abseits`);

  // `power_supply` wird bewusst nicht gewertet — siehe Modulkopf.

  // Ab hier zählt nur, was fürs Übernachten **im Fahrzeug** entscheidet:
  // Sanitär, Untergrund, Gebühr und die Erlaubnis, dort zu stehen. Bei einer
  // Reise mit Unterkunft entfällt der ganze Block — er beantwortet dort keine
  // Frage, die sich jemand stellt, und stünde als Lücke in der Begründung.
  // Bei einer gemischten Reise entfällt er nur für die Unterkünfte; siehe
  // `fahrzeugmassstab`.
  if (imFahrzeug) {
    // Sanitär. Beim Stehen im Auto der zweitwichtigste Punkt.
    if (ja(a.toilets)) {
      punkte += 10;
      gruende.push('WC');
    } else if (p.subcategory === 'stellplatz') {
      einwaende.push('kein WC');
    }
    if (ja(a.shower)) {
      punkte += 5;
      gruende.push('Dusche');
    }
    if (ja(a.drinking_water)) {
      punkte += 3;
      gruende.push('Trinkwasser');
    }
    if (ja(a.sanitary_dump_station)) {
      punkte += 3;
      gruende.push('Entsorgung');
    }

    // Untergrund: Wer im Auto schläft, will eben und fest stehen. Wiese wird
    // nach Regen zum Problem — nicht als Mangel gewertet, aber als Hinweis.
    const boden = a.surface ?? '';
    if (/asphalt|paved|concrete/.test(boden)) {
      punkte += 4;
      gruende.push('befestigt');
    } else if (/grass|dirt|ground|mud/.test(boden)) {
      einwaende.push('Wiese oder Naturboden');
    }

    // Gebühr. Bei Stellplätzen ist kostenfrei ein echtes Plus, bei Hotels
    // bedeutungslos — dort ist der Preis ohnehin nicht in OSM.
    if (p.subcategory !== 'hotel') {
      if (a.fee === 'no') {
        punkte += 6;
        gruende.push('kostenfrei');
      } else if (a.fee === 'yes') {
        einwaende.push('gebührenpflichtig');
      }
    }

    // Ausdrückliche Erlaubnis zum Übernachten — der rechtlich entscheidende
    // Punkt, den kein anderes Merkmal ersetzt.
    if (ja(a.motorhome) || ja(a.caravan) || a.overnight === 'yes') {
      punkte += 8;
      gruende.push('Übernachten erlaubt');
    }
    if (a.maxstay) {
      einwaende.push(`max. Aufenthalt ${a.maxstay}`);
    }
  }

  // Beschränkte Zufahrt **deckelt**, sie zieht nicht ab.
  //
  // Als Abzug modelliert wäre es falsch, und der Test hat das prompt gezeigt:
  // Strom, WC und Dusche bringen zusammen mehr Punkte als jeder vertretbare
  // Abzug — ein abgesperrter Platz mit guter Ausstattung stünde damit vor einem
  // offenen ohne. Ein Platz, den man nicht befahren darf, ist aber kein Platz,
  // egal was daneben steht. Er bleibt sichtbar, weil die OSM-Angabe veraltet
  // sein kann, kann aber nie über einen nutzbaren steigen.
  const gesperrt = a.access === 'private' || a.access === 'no';
  if (gesperrt) einwaende.push('Zufahrt beschränkt');
  const deckel = gesperrt ? 5 : 100;

  return {
    punkte: Math.max(0, Math.min(deckel, Math.round(punkte))),
    gruende,
    einwaende
  };
}

/** Anzeigename der drei gleichrangigen Kategorien. */
export const KATEGORIEN = [
  { key: 'hotel', label: 'Hotel', hinweis: 'Preise und Bewertungen nur über die Website' },
  { key: 'camping', label: 'Campingplatz', hinweis: 'meist mit Sanitär und Dusche' },
  { key: 'stellplatz', label: 'Stellplatz', hinweis: 'zum Übernachten im Auto' }
] as const;
